import { useRef, useState } from 'react';
import { Box, Avatar, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';

/**
 * Subida reusable de logo (organización / proyecto / cargo).
 *
 * Props:
 * - currentUrl: URL actual del logo (o null)
 * - uploadUrl: endpoint POST (multipart, campo "logo")
 * - params: query params opcionales (ej: { organizationId, projectId })
 * - onUploaded(url): callback con la URL resultante
 * - canEdit: muestra el botón de subir
 * - size: tamaño del preview (px)
 * - shape: 'circle' | 'square'
 * - fallback: contenido a mostrar cuando no hay logo (ej. iniciales)
 */
const LogoUploader = ({
  currentUrl = null,
  uploadUrl,
  params = {},
  onUploaded,
  canEdit = true,
  size = 72,
  shape = 'circle',
  fallback = null,
}) => {
  const { t } = useTranslation();
  const { authToken, showAlert } = useStore.getState();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      showAlert(t('branding.invalidImage'), 'error');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post(uploadUrl, fd, {
        params,
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = res.data.response?.data || {};
      const url = data.imgPath || data.url || null;
      if (res.data.response?.status_response === 'OK' && url) {
        if (onUploaded) onUploaded(url);
        showAlert(t('branding.logoUpdated'), 'success');
      } else {
        showAlert(t('branding.uploadError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('branding.uploadError'), 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <Avatar
        src={currentUrl || undefined}
        variant={shape === 'square' ? 'rounded' : 'circular'}
        sx={{ width: size, height: size, bgcolor: '#EDE9FE', color: '#6A5AF9', fontWeight: 700 }}
      >
        {!currentUrl && (fallback || <PhotoCameraIcon />)}
      </Avatar>

      {canEdit && (
        <Tooltip title={t('branding.changeLogo')}>
          <span>
            <IconButton
              size="small"
              onClick={handlePick}
              disabled={uploading}
              sx={{
                position: 'absolute', bottom: -6, right: -6,
                width: 26, height: 26,
                backgroundColor: '#6A5AF9', color: '#fff',
                '&:hover': { backgroundColor: '#5B4DE8' },
              }}
            >
              {uploading
                ? <CircularProgress size={14} color="inherit" />
                : <PhotoCameraIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
    </Box>
  );
};

export default LogoUploader;
